import React, { useState, useRef, useMemo } from 'react';
import { archiveAndEncrypt } from '../services/archiveService';
import { isCryptoSupported } from '../services/cryptoService';

// File size limits (in bytes)
const MAX_TOTAL_SIZE = 500 * 1024 * 1024; // 500 MB max total size
const WARNING_SIZE = 100 * 1024 * 1024; // 100 MB warning threshold

/**
 * Format file size for display
 */
const formatFileSize = bytes => {
  if (bytes === 0) {
    return '0 B';
  }
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Component for selecting, encrypting and downloading files
 */
const FileUploader = () => {
  const [files, setFiles] = useState([]);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [includeReadme, setIncludeReadme] = useState(true);
  const [readmeText, setReadmeText] = useState('');
  const [showReadmeEditor, setShowReadmeEditor] = useState(false);
  const fileInputRef = useRef(null);
  const dirInputRef = useRef(null);
  const dropRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [passwordScore, setPasswordScore] = useState(0);
  const [passwordLabel, setPasswordLabel] = useState('');
  const [compressionLevel, setCompressionLevel] = useState(6);
  const [showPasswordGenerator, setShowPasswordGenerator] = useState(false);
  const [passwordLength, setPasswordLength] = useState(16);
  const [includeNumbers, setIncludeNumbers] = useState(true);
  const [includeSymbols, setIncludeSymbols] = useState(true);
  const [includeUppercase, setIncludeUppercase] = useState(true);
  const [includeLowercase, setIncludeLowercase] = useState(true);

  // Check WebCrypto API support
  const cryptoSupported = isCryptoSupported();

  // Calculate total file size
  const totalFileSize = useMemo(() => {
    return files.reduce((sum, file) => sum + file.size, 0);
  }, [files]);

  // Check if size exceeds limits
  const isSizeExceeded = totalFileSize > MAX_TOTAL_SIZE;
  const isSizeWarning = totalFileSize > WARNING_SIZE && !isSizeExceeded;

  // File selection handler
  const handleFileChange = event => {
    // Merge new files with already selected
    const newFiles = Array.from(event.target.files);
    const allFiles = [...files, ...newFiles];

    // Remove duplicates by path
    const uniqueFiles = [];
    const filePaths = new Set();

    allFiles.forEach(file => {
      const path = file.webkitRelativePath || file.name;
      if (!filePaths.has(path)) {
        filePaths.add(path);
        uniqueFiles.push(file);
      }
    });

    setFiles(uniqueFiles);
    setError(null);

    console.log(`Selected ${uniqueFiles.length} item(s)`);

    // Log structure for debugging
    const fileStructure = uniqueFiles.map(file => ({
      name: file.name,
      path: file.webkitRelativePath || file.name,
      size: file.size,
      type: file.type || 'directory',
    }));

    console.log('Selected file structure:', fileStructure);

    // Reset input value to allow reselecting the same files
    if (event.target) {
      event.target.value = '';
    }
  };

  // Password change
  const handlePasswordChange = event => {
    setPassword(event.target.value);
    updatePasswordStrength(event.target.value);
    setError(null);
  };

  // Confirm password change
  const handleConfirmPasswordChange = event => {
    setConfirmPassword(event.target.value);
    setError(null);
  };

  const updatePasswordStrength = value => {
    const lengthScore = Math.min(2, Math.floor(value.length / 8));
    const variety = [/[a-z]/, /[A-Z]/, /[0-9]/, /[^A-Za-z0-9]/].reduce(
      (acc, rx) => acc + (rx.test(value) ? 1 : 0),
      0
    );
    const varietyScore = Math.max(0, variety - 1);
    const score = Math.max(0, Math.min(4, lengthScore + varietyScore));
    setPasswordScore(score);
    const labels = ['Very weak', 'Weak', 'Medium', 'Good', 'Strong'];
    setPasswordLabel(labels[score] || '');
  };

  const generatePassword = (len = 16) => {
    // Собираем набор символов на основе выбранных опций
    let charset = '';
    const lowers = 'abcdefghijklmnopqrstuvwxyz';
    const uppers = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const digits = '0123456789';
    const symbols = '!@#$%^&*()-_=+[]{};:,.<>?';

    const requiredChars = [];

    if (includeLowercase) {
      charset += lowers;
      requiredChars.push(lowers[Math.floor(Math.random() * lowers.length)]);
    }
    if (includeUppercase) {
      charset += uppers;
      requiredChars.push(uppers[Math.floor(Math.random() * uppers.length)]);
    }
    if (includeNumbers) {
      charset += digits;
      requiredChars.push(digits[Math.floor(Math.random() * digits.length)]);
    }
    if (includeSymbols) {
      charset += symbols;
      requiredChars.push(symbols[Math.floor(Math.random() * symbols.length)]);
    }

    // Если ничего не выбрано, используем все
    if (charset === '') {
      charset = lowers + uppers + digits + symbols;
      requiredChars.push(
        lowers[Math.floor(Math.random() * lowers.length)],
        uppers[Math.floor(Math.random() * uppers.length)],
        digits[Math.floor(Math.random() * digits.length)],
        symbols[Math.floor(Math.random() * symbols.length)]
      );
    }

    // Генерируем пароль
    const arr = new Uint32Array(len - requiredChars.length);
    window.crypto.getRandomValues(arr);
    const randomChars = Array.from(arr).map(n => charset[n % charset.length]);

    // Объединяем обязательные и случайные символы, перемешиваем
    const allChars = [...requiredChars, ...randomChars];
    const shuffled = allChars.sort(() => Math.random() - 0.5).join('');

    setPassword(shuffled);
    setConfirmPassword(shuffled);
    updatePasswordStrength(shuffled);
  };

  const onDragOver = e => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };
  const onDragLeave = e => {
    e.preventDefault();
    e.stopPropagation();
    if (e.target === dropRef.current) {
      setIsDragging(false);
    }
  };
  const onDrop = async e => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    try {
      const dt = e.dataTransfer;
      if (dt.items && dt.items.length && dt.items[0].webkitGetAsEntry) {
        const newFiles = [];
        const traverse = async (entry, path = '') => {
          if (entry.isFile) {
            await new Promise(resolve => {
              entry.file(f => {
                const fullName = path ? `${path}/${f.name}` : f.name;
                const f2 = new File([f], fullName, { type: f.type, lastModified: f.lastModified });
                newFiles.push(f2);
                resolve();
              });
            });
          } else if (entry.isDirectory) {
            const reader = entry.createReader();
            await new Promise((resolve, reject) => {
              const read = () => {
                reader.readEntries(async entries => {
                  if (!entries.length) {
                    return resolve();
                  }
                  for (const ent of entries) {
                    await traverse(ent, path ? `${path}/${entry.name}` : entry.name);
                  }
                  read();
                }, reject);
              };
              read();
            });
          }
        };
        for (const item of dt.items) {
          const entry = item.webkitGetAsEntry();
          if (entry) {
            await traverse(entry);
          }
        }
        mergeNewFiles(newFiles);
      } else {
        const flist = Array.from(dt.files || []);
        mergeNewFiles(flist);
      }
    } catch (err) {
      console.error('Drop error:', err);
      setError('Failed to process dropped items');
    }
  };

  const mergeNewFiles = incoming => {
    const allFiles = [...files, ...incoming];
    const unique = [];
    const paths = new Set();
    for (const f of allFiles) {
      const p = f.webkitRelativePath || f.name;
      if (!paths.has(p)) {
        paths.add(p);
        unique.push(f);
      }
    }
    setFiles(unique);
  };

  /** Simple validation before submit */
  const validateForm = () => {
    if (!files.length) {
      setError('Please select files to encrypt');
      return false;
    }

    // Check total file size
    if (isSizeExceeded) {
      setError(
        `Total file size (${formatFileSize(totalFileSize)}) exceeds maximum limit of ${formatFileSize(MAX_TOTAL_SIZE)}. Please select fewer or smaller files.`
      );
      return false;
    }

    if (!password) {
      setError('Please enter a password');
      return false;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return false;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return false;
    }

    return true;
  };

  /** Include README toggle */
  const handleIncludeReadmeChange = event => {
    setIncludeReadme(event.target.checked);
  };


  /** README text change */
  const handleReadmeTextChange = event => {
    setReadmeText(event.target.value);
  };

  /** Submit */
  const handleSubmit = async event => {
    event.preventDefault();

    if (!cryptoSupported) {
      setError('Your browser does not support Web Crypto API. Please use a modern browser.');
      return;
    }

    if (!validateForm()) {
      return;
    }

    try {
      setIsUploading(true);
      setUploadProgress(0);
      setError(null);

      console.log('Starting archive + encryption:', files);

      // Ensure files are selected
      if (!files || files.length === 0) {
        throw new Error('No files selected for archiving');
      }

      // Ensure array contains File objects
      const validFiles = Array.from(files).filter(file => file instanceof File);

      if (validFiles.length === 0) {
        throw new Error('Selected files are invalid');
      }

      if (validFiles.length !== files.length) {
        console.warn(`Some items are not files. Processing ${validFiles.length} of ${files.length}`);
      }

      // Архивируем и шифруем файлы
      console.log('Archiving and encrypting files...');
      setUploadProgress(25);

      // Build archive options
      const archiveOptions = {
        includeReadme: includeReadme,
        readmeText: readmeText,
        compressionLevel: Number(compressionLevel),
      };

      const encryptedData = await archiveAndEncrypt(validFiles, password, archiveOptions);
      setUploadProgress(75);
      console.log('Files encrypted successfully');

      // Create a Blob with proper ZIP MIME type
      const encryptedBlob = new Blob([encryptedData.encryptedData], { type: 'application/zip' });

      // Build file name with date
      const now = new Date();
      const dateStr = now.toISOString().slice(0, 10).replace(/-/g, '');
      const timeStr = now.toTimeString().slice(0, 8).replace(/:/g, '');
      const fileName = `encrypted_archive_${dateStr}_${timeStr}.zip`;

      // Create a download link
      const downloadUrl = URL.createObjectURL(encryptedBlob);
      const downloadLink = document.createElement('a');
      downloadLink.href = downloadUrl;
      downloadLink.download = fileName;

      // Append link, click and cleanup
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);

      // Revoke URL
      URL.revokeObjectURL(downloadUrl);

      // All operations are local. No network requests.

      setUploadProgress(100);
      console.log('Encrypted archive ready for download');

      // Reset form
      setFiles([]);
      setPassword('');
      setConfirmPassword('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Error while processing files:', error);
      let errorMessage = 'An error occurred while processing files';

      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'object') {
        try {
          errorMessage = JSON.stringify(error);
        } catch (e) {
          errorMessage = 'Unknown error';
        }
      } else if (error) {
        errorMessage = String(error);
      }

      setError(`Error processing files: ${errorMessage}`);
    } finally {
      setIsUploading(false);
    }
  };

  /** Render selected file list */
  const renderFileList = () => {
    if (!files.length) {
      return <p>No files selected</p>;
    }

    // Group files by root directory
    const fileStructure = {};

    files.forEach(file => {
      const path = file.webkitRelativePath || file.name;
      const parts = path.split('/');

      // Если это файл из каталога
      if (parts.length > 1) {
        const rootDir = parts[0];

        if (!fileStructure[rootDir]) {
          fileStructure[rootDir] = {
            isDirectory: true,
            files: [],
            size: 0,
          };
        }

        fileStructure[rootDir].files.push(file);
        fileStructure[rootDir].size += file.size;
      } else {
        // Если это отдельный файл
        if (!fileStructure[file.name]) {
          fileStructure[file.name] = {
            isDirectory: false,
            file: file,
            size: file.size,
          };
        }
      }
    });

    return (
      <div className="selected-files">
        <h3>Selected files and folders ({files.length} items):</h3>

        {/* Total size indicator with warning/error states */}
        <div
          className={`size-indicator ${isSizeExceeded ? 'size-error' : isSizeWarning ? 'size-warning' : ''}`}
        >
          <span>Total size: {formatFileSize(totalFileSize)}</span>
          {isSizeExceeded && (
            <span className="size-limit-message">
              Exceeds {formatFileSize(MAX_TOTAL_SIZE)} limit
            </span>
          )}
          {isSizeWarning && (
            <span className="size-limit-message">
              Large files may take longer to process
            </span>
          )}
        </div>

        <ul className="file-list">
          {Object.entries(fileStructure).map(([name, data], index) => (
            <li key={index} className={`file-item ${data.isDirectory ? 'directory-item' : ''}`}>
              {data.isDirectory ? (
                <>
                  <span className="directory-name">📁 {name}</span>
                  <span className="file-count">({data.files.length} files)</span>
                  <span className="file-size">({formatFileSize(data.size)})</span>
                </>
              ) : (
                <>
                  <span className="file-name">📄 {name}</span>
                  <span className="file-size">({formatFileSize(data.size)})</span>
                </>
              )}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  /**
   * Отображение индикатора прогресса
   */
  const renderProgressBar = () => {
    if (!isUploading) {
      return null;
    }

    return (
      <div className="progress-container">
        <div className="progress-bar" style={{ width: `${uploadProgress}%` }}></div>
        <div className="progress-text">{uploadProgress}%</div>
      </div>
    );
  };

  if (!cryptoSupported) {
    return (
      <div className="error-container">
        <h3>Compatibility issue</h3>
        <p>Your browser does not support Web Crypto API required to encrypt files.</p>
        <p>Please use a modern browser like Chrome, Firefox, Safari or Edge.</p>
      </div>
    );
  }

  return (
    <div className="file-uploader">
      <h2>Encrypt and Archive Files</h2>

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="file-input">Select files to encrypt:</label>
          <div className="file-input-container">
            {/* Скрытый input для выбора файлов */}
            <input
              type="file"
              id="file-input"
              multiple
              onChange={handleFileChange}
              disabled={isUploading}
              ref={fileInputRef}
              style={{ display: 'none' }}
            />

            {/* Скрытый input для выбора каталогов */}
            <input
              type="file"
              id="dir-input"
              multiple
              webkitdirectory="true"
              onChange={handleFileChange}
              disabled={isUploading}
              ref={dirInputRef}
              style={{ display: 'none' }}
            />

            <div className="button-group">
              <button
                type="button"
                className="action-button select-files-button"
                onClick={() => fileInputRef.current.click()}
                disabled={isUploading}
              >
                <span className="button-icon">📄</span>
                <span className="button-text">Choose files</span>
              </button>

              <button
                type="button"
                className="action-button select-dir-button"
                onClick={() => dirInputRef.current.click()}
                disabled={isUploading}
              >
                <span className="button-icon">📁</span>
                <span className="button-text">Choose folder</span>
              </button>
            </div>

            <div
              ref={dropRef}
              className={`dropzone ${isDragging ? 'dragover' : ''}`}
              onDragOver={onDragOver}
              onDragEnter={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
            >
              <div className="dropzone-inner">📥 Or drag files/folders here</div>
            </div>

            <div className="button-group">

              {files.length > 0 && (
                <button
                  type="button"
                  className="action-button clear-files-button"
                  onClick={() => {
                    setFiles([]);
                    if (fileInputRef.current) {
                      fileInputRef.current.value = '';
                    }
                    if (dirInputRef.current) {
                      dirInputRef.current.value = '';
                    }
                  }}
                  disabled={isUploading}
                >
                  <span className="button-icon">🗑️</span>
                  <span className="button-text">Clear</span>
                </button>
              )}
            </div>

            <style>{`
              .button-group {
                display: flex;
                gap: 10px;
                margin-bottom: 10px;
                flex-wrap: wrap;
              }

              .action-button {
                display: flex;
                align-items: center;
                justify-content: center;
                padding: 10px 15px;
                border-radius: 6px;
                border: 1px solid #ccc;
                background: linear-gradient(to bottom, #ffffff, #f5f5f5);
                cursor: pointer;
                transition: all 0.2s ease;
                font-weight: 500;
                box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
              }

              .action-button:hover {
                background: linear-gradient(to bottom, #f8f8f8, #e9e9e9);
                border-color: #999;
              }

              .action-button:active {
                background: linear-gradient(to bottom, #e9e9e9, #f8f8f8);
                box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.1);
              }

              .action-button:disabled {
                opacity: 0.6;
                cursor: not-allowed;
              }

              .button-icon {
                margin-right: 8px;
                font-size: 1.2em;
              }

              .select-files-button {
                background: linear-gradient(to bottom, #f0f8ff, #e6f2ff);
                border-color: #b3d9ff;
                color: #0066cc;
              }

              .select-files-button:hover {
                background: linear-gradient(to bottom, #e6f2ff, #d9ecff);
                border-color: #80bfff;
              }

              .select-dir-button {
                background: linear-gradient(to bottom, #f0fff0, #e6ffe6);
                border-color: #b3ffb3;
                color: #009900;
              }

              .select-dir-button:hover {
                background: linear-gradient(to bottom, #e6ffe6, #d9ffd9);
                border-color: #80ff80;
              }

              .clear-files-button {
                background: linear-gradient(to bottom, #fff0f0, #ffe6e6);
                border-color: #ffb3b3;
                color: #cc0000;
              }

              .clear-files-button:hover {
                background: linear-gradient(to bottom, #ffe6e6, #ffd9d9);
                border-color: #ff8080;
              }

              .readme-options {
                margin-top: 20px;
                padding: 15px;
                background-color: var(--secondary-bg);
                border-radius: 8px;
                border: 1px solid var(--border-color);
              }

              .checkbox-container {
                display: flex;
                align-items: center;
                margin-bottom: 10px;
              }

              .checkbox-container input[type='checkbox'] {
                margin-right: 10px;
                width: 18px;
                height: 18px;
              }

              .readme-editor-container {
                margin-top: 10px;
              }

              .toggle-readme-button {
                background: none;
                border: none;
                color: var(--accent-color);
                cursor: pointer;
                padding: 5px 0;
                font-size: 14px;
                text-decoration: underline;
              }

              .toggle-readme-button:hover {
                color: var(--accent-hover);
              }

              .readme-editor {
                margin-top: 10px;
                padding: 10px;
                background-color: var(--card-bg);
                border-radius: 4px;
                border: 1px solid var(--border-color);
              }

              .readme-info {
                margin-bottom: 10px;
                font-size: 14px;
                color: #666;
              }

              .readme-textarea {
                width: 100%;
                padding: 10px;
                border: 1px solid #ccc;
                border-radius: 4px;
                font-family: inherit;
                font-size: 14px;
                resize: vertical;
              }
            `}</style>
          </div>
          <div className="input-help-text">Pick files and folders you want to encrypt</div>
        </div>

        {renderFileList()}

        <div className="form-group">
          <label htmlFor="password-input">Encryption password:</label>
          <div className="password-controls">
            <div className="password-row">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password-input"
                value={password}
                onChange={handlePasswordChange}
                disabled={isUploading}
                className="password-input"
                placeholder="Enter a strong password"
              />
              <button
                type="button"
                className="inline-btn"
                onClick={() => setShowPassword(s => !s)}
                title={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? '🙈' : '👁️'}
              </button>
            </div>
            <button
              type="button"
              className="inline-btn generate-btn"
              onClick={() => setShowPasswordGenerator(!showPasswordGenerator)}
              title="Password generator"
            >
              🎲
            </button>
          </div>

          {showPasswordGenerator && (
            <div className="password-generator">
              <div className="generator-header">
                <label htmlFor="password-length">Password length: {passwordLength}</label>
                <input
                  type="range"
                  id="password-length"
                  min="8"
                  max="32"
                  value={passwordLength}
                  onChange={e => setPasswordLength(Number(e.target.value))}
                  className="password-length-slider"
                />
              </div>

              <div className="generator-options">
                <label className="checkbox-option">
                  <input
                    type="checkbox"
                    checked={includeLowercase}
                    onChange={e => setIncludeLowercase(e.target.checked)}
                  />
                  <span>Lowercase (a-z)</span>
                </label>

                <label className="checkbox-option">
                  <input
                    type="checkbox"
                    checked={includeUppercase}
                    onChange={e => setIncludeUppercase(e.target.checked)}
                  />
                  <span>Uppercase (A-Z)</span>
                </label>

                <label className="checkbox-option">
                  <input
                    type="checkbox"
                    checked={includeNumbers}
                    onChange={e => setIncludeNumbers(e.target.checked)}
                  />
                  <span>Digits (0-9)</span>
                </label>

                <label className="checkbox-option">
                  <input
                    type="checkbox"
                    checked={includeSymbols}
                    onChange={e => setIncludeSymbols(e.target.checked)}
                  />
                  <span>Symbols (!@#$...)</span>
                </label>
              </div>

              <button
                type="button"
                className="btn btn-primary generator-generate-btn"
                onClick={() => generatePassword(passwordLength)}
              >
                🎲 Generate password
              </button>
            </div>
          )}

          {password && (
            <div className={`pw-strength pw-s${passwordScore}`}>
              <div className="pw-bar" style={{ width: `${(passwordScore + 1) * 20}%` }} />
              <span className="pw-label">{passwordLabel}</span>
            </div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="confirm-password-input">Confirm password:</label>
          <div className="password-row">
            <input
              type={showConfirm ? 'text' : 'password'}
              id="confirm-password-input"
              value={confirmPassword}
              onChange={handleConfirmPasswordChange}
              disabled={isUploading}
              className="password-input"
              placeholder="Repeat password"
            />
            <button
              type="button"
              className="inline-btn"
              onClick={() => setShowConfirm(s => !s)}
              title={showConfirm ? 'Скрыть' : 'Показать'}
            >
              {showConfirm ? '🙈' : '👁️'}
            </button>
          </div>
        </div>

        <div className="form-group readme-options">
          <div className="checkbox-container">
            <input
              type="checkbox"
              id="include-readme"
              checked={includeReadme}
              onChange={handleIncludeReadmeChange}
              disabled={isUploading}
            />
            <label htmlFor="include-readme">Include README.txt in archive</label>
          </div>

          {/* Без личного кабинета: только локальные операции */}

          {includeReadme && (
            <div className="readme-editor-container">
              <button
                type="button"
                className="toggle-readme-button"
                onClick={() => setShowReadmeEditor(!showReadmeEditor)}
                disabled={isUploading}
              >
                {showReadmeEditor ? 'Hide editor' : 'Customize README.txt'}
              </button>

              {showReadmeEditor && (
                <div className="readme-editor">
                  <p className="readme-info">Enter additional text for README.txt. Standard archive info will be added automatically.</p>
                  <textarea
                    value={readmeText}
                    onChange={handleReadmeTextChange}
                    disabled={isUploading}
                    placeholder="Enter additional information for README.txt..."
                    rows={5}
                    className="readme-textarea"
                  />
                </div>
              )}
            </div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="compression-select">Compression level:</label>
          <select
            id="compression-select"
            value={compressionLevel}
            onChange={e => setCompressionLevel(Number(e.target.value))}
            disabled={isUploading}
            className="select-input"
          >
            <option value={1}>⚡ Fast (L1) — minimal compression, max speed</option>
            <option value={6}>⚖️ Balanced (L6) — good size/speed ratio</option>
            <option value={9}>📦 Maximum (L9) — best compression, slower</option>
          </select>
          <div className="compression-info">
            <small className="muted">
              {compressionLevel === 1 && '⚡ Faster creation, larger file size'}
              {compressionLevel === 6 && '⚖️ Recommended for most cases'}
              {compressionLevel === 9 && '📦 Best compression, takes longer'}
            </small>
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}

        {renderProgressBar()}

        <div className="form-actions">
          <button type="submit" disabled={isUploading || !files.length} className="upload-button">
            {isUploading ? 'Processing…' : 'Encrypt & Download'}
          </button>
        </div>
      </form>

      <div className="security-note">
        <h4>Security notes:</h4>
        <ul>
          <li>Files are encrypted entirely in your browser</li>
          <li>No encrypted data is sent to a server</li>
          <li>All processing happens on your device</li>
          <li>Use a strong password and store it safely</li>
          <li>Lost password cannot be recovered</li>
        </ul>
      </div>
    </div>
  );
};

export default FileUploader;
